import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Pill } from '../../components/ui';
import { displayValue } from '../../components/titleAnalysis/displayUtils/displayValue';
import { displayList } from '../../components/titleAnalysis/displayUtils/displayList';
import {
  formatConfidence,
  formatSeverity,
} from '../../components/titleAnalysis/displayUtils/labelFormatters';
import {
  SEVERITY_FILTERS,
  categoryLabel,
  filterExceptionsBySeverity,
  severityCounts,
  severityTone,
  sortExceptionsBySeverity,
  type ReportException,
  type SeverityFilter,
} from './reportModel';
import { TabEmpty } from './reportParts';

/**
 * ExceptionsTab: the Exceptions tab of the Title analysis report.
 *
 * A severity filter (All, Material, Curable, Informational, each with a count)
 * sits above the list. The list is ordered material, then curable, then
 * informational. Each row reads left to right: severity tag, exception id,
 * category, title, chevron. Rows are collapsed by default.
 * Expanding a row shows the description and evidence on the left, and a
 * curative action card (with confidence, rule and related exceptions) on the
 * right, plus any assumptions.
 *
 * Local UI state only. `initialExpandedId` opens one exception on first render
 * (for the Workspace `exception` URL param).
 *
 * Requirement: 11.9.
 */
export interface ExceptionsTabProps {
  exceptions: ReportException[];
  initialExpandedId?: string | null;
}

export const ExceptionsTab: React.FC<ExceptionsTabProps> = ({
  exceptions,
  initialExpandedId = null,
}) => {
  const [filter, setFilter] = useState<SeverityFilter>('all');
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(initialExpandedId ? [initialExpandedId] : [])
  );
  const filterRefs = useRef<Partial<Record<SeverityFilter, HTMLButtonElement | null>>>({});

  const counts = useMemo(() => severityCounts(exceptions), [exceptions]);
  const visible = useMemo(
    () => sortExceptionsBySeverity(filterExceptionsBySeverity(exceptions, filter)),
    [exceptions, filter]
  );

  const toggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  /** Open a related exception: widen the filter if needed, expand it and scroll to it. */
  const openRelated = useCallback(
    (id: string) => {
      const target = exceptions.find((e) => e.exceptionId === id);
      if (!target) return;
      if (filter !== 'all' && target.severity !== filter) setFilter('all');
      setExpanded((prev) => new Set(prev).add(id));
      requestAnimationFrame(() => {
        const el = document.getElementById(`exception-row-${id}`);
        if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
      });
    },
    [exceptions, filter]
  );

  const onFilterKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      const last = SEVERITY_FILTERS.length - 1;
      let next = index;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        next = index === last ? 0 : index + 1;
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        next = index === 0 ? last : index - 1;
      } else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = last;
      else return;
      event.preventDefault();
      const id = SEVERITY_FILTERS[next].id;
      setFilter(id);
      filterRefs.current[id]?.focus();
    },
    []
  );

  if (exceptions.length === 0) {
    return <TabEmpty>No exceptions found.</TabEmpty>;
  }

  return (
    <div data-testid="exceptions-tab" className="flex flex-col gap-4">
      <div
        role="radiogroup"
        aria-label="Filter exceptions by severity"
        className="flex flex-wrap gap-2"
      >
        {SEVERITY_FILTERS.map(({ id, label }, index) => {
          const selected = filter === id;
          return (
            <button
              key={id}
              ref={(el) => {
                filterRefs.current[id] = el;
              }}
              type="button"
              role="radio"
              aria-checked={selected}
              data-testid={`severity-filter-${id}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => setFilter(id)}
              onKeyDown={(e) => onFilterKeyDown(e, index)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[13px] font-bold focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB] ${
                selected
                  ? 'border-[#834DFB] bg-[#EDE7FF] text-[#5A21C9]'
                  : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {label}
              <span
                className={`rounded-md px-1.5 text-xs ${
                  selected ? 'bg-white text-[#5A21C9]' : 'bg-[#f3f4f6] text-[#4b5563]'
                }`}
              >
                {counts[id]}
              </span>
            </button>
          );
        })}
      </div>

      {visible.length === 0 ? (
        <TabEmpty>No {filter} exceptions.</TabEmpty>
      ) : (
        <ul className="flex flex-col divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200 bg-white">
          {visible.map((exception) => (
            <ExceptionItem
              key={exception.exceptionId}
              exception={exception}
              isExpanded={expanded.has(exception.exceptionId)}
              onToggle={toggle}
              onOpenRelated={openRelated}
              knownIds={exceptions}
            />
          ))}
        </ul>
      )}
    </div>
  );
};

interface ExceptionItemProps {
  exception: ReportException;
  isExpanded: boolean;
  onToggle: (id: string) => void;
  onOpenRelated: (id: string) => void;
  knownIds: ReportException[];
}

const ExceptionItem: React.FC<ExceptionItemProps> = ({
  exception,
  isExpanded,
  onToggle,
  onOpenRelated,
  knownIds,
}) => {
  const id = exception.exceptionId;
  const panelId = `exception-panel-${id}`;
  const Chevron = isExpanded ? ChevronDown : ChevronRight;

  return (
    <li
      id={`exception-row-${id}`}
      data-testid="exception-item"
      data-expanded={isExpanded ? 'true' : 'false'}
    >
      <button
        type="button"
        aria-expanded={isExpanded}
        aria-controls={isExpanded ? panelId : undefined}
        onClick={() => onToggle(id)}
        className="flex w-full items-center gap-4 px-4 py-3.5 text-left hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#834DFB]"
      >
        <Pill tone={severityTone(exception.severity)} className="w-[92px] justify-center">
          {formatSeverity(exception.severity)}
        </Pill>
        <span className="w-16 flex-shrink-0 font-mono text-xs text-gray-600">{id}</span>
        <span className="w-28 flex-shrink-0 text-xs text-gray-600">
          {categoryLabel(exception.category)}
        </span>
        <span className="min-w-0 flex-1 text-[15px] font-bold text-gray-900">
          {exception.title}
        </span>
        <Chevron className="h-4 w-4 flex-shrink-0 text-gray-500" aria-hidden="true" />
      </button>

      {isExpanded ? (
        <div
          id={panelId}
          role="region"
          aria-label={`${exception.title} details`}
          data-testid="exception-details"
          className="grid grid-cols-1 gap-4 pb-4 pl-[124px] pr-4 md:grid-cols-2"
        >
          <div className="min-w-0">
            <p className="text-sm text-gray-700">{displayValue(exception.description)}</p>

            <div className="mt-3">
              <div className="text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500">
                Evidence
              </div>
              {exception.evidence.length > 0 ? (
                <ul className="mt-1 flex flex-col gap-1">
                  {exception.evidence.map((ev, idx) => (
                    <li key={idx} className="text-[13px] text-gray-700">
                      {ev.instrumentId ? (
                        <span className="font-mono text-xs text-gray-900">
                          {ev.instrumentId}
                          {ev.page !== null ? ` p.${ev.page}` : ''}
                        </span>
                      ) : ev.page !== null ? (
                        <span className="font-mono text-xs text-gray-900">p.{ev.page}</span>
                      ) : null}
                      {ev.instrumentId || ev.page !== null ? ' \u2014 ' : null}
                      {ev.fact}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[13px] text-gray-600">No evidence cited.</p>
              )}
            </div>

            {exception.assumptions.length > 0 ? (
              <div className="mt-3 text-[13px]">
                <div className="text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500">
                  Assumptions
                </div>
                <div className="text-gray-700">{displayList(exception.assumptions)}</div>
              </div>
            ) : null}
          </div>

          <div className="min-w-0 rounded-xl border border-gray-100 bg-[#f9fafb] p-3.5">
            <div className="text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500">
              Curative action
            </div>
            <p className="mt-1 text-sm font-bold text-gray-900">
              {displayValue(exception.curativeAction)}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-600">
              <span>Confidence {formatConfidence(exception.confidence)}</span>
              <span aria-hidden="true">&middot;</span>
              <span>
                Rule <span className="font-mono">{exception.ruleCode}</span>
              </span>
              {exception.relatedExceptionIds.length > 0 ? (
                <>
                  <span aria-hidden="true">&middot;</span>
                  <span>Related</span>
                  {exception.relatedExceptionIds.map((relatedId) =>
                    knownIds.some((e) => e.exceptionId === relatedId) ? (
                      <button
                        key={relatedId}
                        type="button"
                        data-testid="related-exception"
                        onClick={() => onOpenRelated(relatedId)}
                        className="rounded-full bg-[#EDE7FF] px-2 py-0.5 font-mono text-xs font-bold text-[#5A21C9] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#834DFB]"
                      >
                        {relatedId}
                      </button>
                    ) : (
                      <span
                        key={relatedId}
                        className="rounded-full bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700"
                      >
                        {relatedId}
                      </span>
                    )
                  )}
                </>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </li>
  );
};

export default ExceptionsTab;
